import type { ColumnDef } from '@tanstack/react-table';
import { Anchor, Plus } from 'lucide-react';
import { useDeferredValue, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/auth/store';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { VesselSelect } from '@/components/ui/vessel-select';
import {
  useDrydockCampaignsByCompany,
  useDrydockCampaignsByVessel,
} from '@/features/drydock-campaigns/queries';
import {
  DRYDOCK_CAMPAIGN_STATUSES,
  statusVisualTone,
  type DrydockCampaign,
  type DrydockCampaignStatus,
} from '@/features/drydock-campaigns/types';
import { useVessels } from '@/features/vessels/queries';
import { vesselDisplayName, type Vessel } from '@/features/vessels/types';
import { ApiError } from '@/lib/api';
import { matchesQuery } from '@/lib/list-search';
import { cn } from '@/lib/utils';

function ListSkeleton() {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {Array.from({ length: 6 }).map((_, i) => (
            <TableHead key={i}>
              <Skeleton className="h-3 w-20" />
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: 5 }).map((_, r) => (
          <TableRow key={r}>
            {Array.from({ length: 6 }).map((_, c) => (
              <TableCell key={c}>
                <Skeleton className="h-4 w-full" />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

/**
 * Format an amount + currency triple. Falls back to '—' when either
 * side is missing — backend allows budgets to be partially filled or
 * absent entirely.
 */
function formatBudget(
  amount: string | null,
  currency: string | null,
): string {
  if (!amount || !currency) return '—';
  const n = Number(amount);
  if (!Number.isFinite(n)) return `${amount} ${currency}`;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `${n.toLocaleString()} ${currency}`;
  }
}

const STATUS_TONE_CLASS: Record<
  ReturnType<typeof statusVisualTone>,
  string
> = {
  emerald: 'bg-emerald-100 text-emerald-700',
  amber: 'bg-amber-100 text-amber-800',
  red: 'bg-red-100 text-red-700',
  muted: 'bg-slate-100 text-slate-700',
};

function StatusBadge({
  status,
  label,
}: {
  status: DrydockCampaignStatus;
  label: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
        STATUS_TONE_CLASS[statusVisualTone(status)],
      )}
    >
      {label}
    </span>
  );
}

export function DrydockCampaignsListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);

  const vesselId = searchParams.get('vesselId') ?? undefined;
  const statusParam = searchParams.get('status') ?? undefined;
  const statusFilter: DrydockCampaignStatus | undefined =
    statusParam &&
    (DRYDOCK_CAMPAIGN_STATUSES as readonly string[]).includes(statusParam)
      ? (statusParam as DrydockCampaignStatus)
      : undefined;

  const effectiveCompanyId = user?.companyId ?? undefined;

  // Both hooks always mount to keep the order stable, but only one is
  // enabled — vessel scope wins when ?vesselId is present.
  const useVesselScope = Boolean(vesselId);
  const useCompanyScope = !useVesselScope;

  const byVessel = useDrydockCampaignsByVessel(
    useVesselScope ? vesselId : undefined,
    statusFilter ? { status: statusFilter } : {},
  );
  const byCompany = useDrydockCampaignsByCompany(
    useCompanyScope ? effectiveCompanyId : undefined,
    statusFilter ? { status: statusFilter } : {},
  );

  const active = useVesselScope ? byVessel : byCompany;
  const { data, isLoading, isError, error, refetch, isFetching } = active;

  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);

  const filtered = useMemo(() => {
    const list = data ?? [];
    return list.filter((c) =>
      matchesQuery(c, deferredSearch, [
        (x) => x.title,
        (x) => x.description,
        (x) => x.shipyard,
      ]),
    );
  }, [data, deferredSearch]);

  const { data: vessels } = useVessels();

  const vesselsMap = useMemo(() => {
    const m = new Map<string, Vessel>();
    (vessels ?? []).forEach((v) => m.set(v.id, v));
    return m;
  }, [vessels]);

  const setFilter = (key: string, next: string | null) => {
    const params = new URLSearchParams(searchParams);
    if (next === null || next === '') params.delete(key);
    else params.set(key, next);
    setSearchParams(params, { replace: true });
  };

  const columns = useMemo<ColumnDef<DrydockCampaign>[]>(
    () => [
      {
        id: 'title',
        header: () => t('drydockCampaign.columns.title'),
        cell: ({ row }) => (
          <Link
            to={`/drydock-campaigns/${row.original.id}`}
            onClick={(e) => e.stopPropagation()}
            className="font-medium text-primary hover:underline"
          >
            {row.original.title}
          </Link>
        ),
      },
      {
        id: 'vessel',
        header: () => t('drydockCampaign.columns.vessel'),
        cell: ({ row }) => {
          const v = vesselsMap.get(row.original.vesselId);
          return v ? vesselDisplayName(v) : row.original.vesselId;
        },
      },
      {
        accessorKey: 'startDate',
        header: () => t('drydockCampaign.columns.startDate'),
        cell: ({ getValue }) => formatDate(getValue<string | null>()),
      },
      {
        accessorKey: 'endDate',
        header: () => t('drydockCampaign.columns.endDate'),
        cell: ({ getValue }) => formatDate(getValue<string | null>()),
      },
      {
        id: 'shipyard',
        header: () => t('drydockCampaign.columns.shipyard'),
        cell: ({ row }) => row.original.shipyard ?? '—',
      },
      {
        id: 'budget',
        header: () => t('drydockCampaign.columns.budget'),
        cell: ({ row }) => (
          <span className="tabular-nums">
            {formatBudget(
              row.original.totalBudgetAmount,
              row.original.totalBudgetCurrency,
            )}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: () => t('drydockCampaign.columns.status'),
        cell: ({ getValue }) => {
          const s = getValue<DrydockCampaignStatus>();
          return (
            <StatusBadge
              status={s}
              label={t(`drydockCampaign.status.${s}`)}
            />
          );
        },
      },
    ],
    [t, vesselsMap],
  );

  const errorMessage = (() => {
    if (!isError) return null;
    if (error instanceof ApiError) {
      return t(error.key, {
        defaultValue: t('drydockCampaign.list.loadError'),
        ...(error.params ?? {}),
      });
    }
    return t('drydockCampaign.list.loadError');
  })();

  const missingCompanyContext = !useVesselScope && !effectiveCompanyId;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">
          {t('drydockCampaign.list.title')}
        </h1>
        <Button asChild>
          <Link to="/drydock-campaigns/new">
            <Plus aria-hidden />
            {t('drydockCampaign.list.newButton')}
          </Link>
        </Button>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">{t('common.search')}</span>
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('drydockCampaign.list.searchPlaceholder')}
            className="h-9 w-64"
            data-testid="drydock-campaigns-search"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">
            {t('drydockCampaign.list.filterByVessel')}
          </span>
          <VesselSelect
            value={vesselId ?? null}
            onChange={(v) => setFilter('vesselId', v)}
            allowEmpty
            emptyLabel={t('drydockCampaign.list.allVessels')}
            className="h-9 w-56"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">
            {t('drydockCampaign.list.filterByStatus')}
          </span>
          <select
            value={statusFilter ?? ''}
            onChange={(e) => setFilter('status', e.target.value || null)}
            className="flex h-9 w-44 rounded-md border border-input bg-background px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="">{t('drydockCampaign.list.allStatuses')}</option>
            {DRYDOCK_CAMPAIGN_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`drydockCampaign.status.${s}`)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {missingCompanyContext && (
        <Alert>
          <AlertTitle>{t('drydockCampaign.list.noCompanyTitle')}</AlertTitle>
          <AlertDescription>
            {t('drydockCampaign.list.noCompanyHint')}
          </AlertDescription>
        </Alert>
      )}

      {errorMessage && (
        <Alert variant="destructive">
          <AlertTitle>{t('drydockCampaign.list.loadError')}</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>{errorMessage}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {t('common.retry')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {!isError && !missingCompanyContext && (
        <DataTable
          columns={columns}
          data={filtered}
          isLoading={isLoading}
          loadingState={<ListSkeleton />}
          emptyState={
            <EmptyState
              icon={Anchor}
              title={t('drydockCampaign.list.empty')}
              description={t('drydockCampaign.list.emptyHint')}
              action={
                <Button asChild size="sm">
                  <Link to="/drydock-campaigns/new">
                    <Plus aria-hidden />
                    {t('drydockCampaign.list.newButton')}
                  </Link>
                </Button>
              }
            />
          }
          onRowClick={(c) => navigate(`/drydock-campaigns/${c.id}`)}
        />
      )}
    </div>
  );
}
